/*
 * CALCULATE-STAGE.JS - Stage Total Calculation Utility for Project Management
 *
 * RECENT CHANGES - Variation Calculation Exclusion Enhancement
 *
 * SUMMARY:
 * Enhanced stage total calculation to exclude variations from stage-level financial calculations
 * while maintaining accuracy in project-level totals. Prevents double-counting of variations
 * in stage summaries.
 *
 * MAJOR MODIFICATIONS:
 * - processTasks() - Added variation exclusion logic using isVariation and isOption flags
 * - Variations excluded from stage calculations but included in project totals
 * - Enhanced task filtering to prevent double-counting of variation costs
 */

import { roundValue, toDecimal } from "../decimal-utils";

export const stageTotal = (stages) => {
  // Holds totals for each stage (by stageId)
  const stageTotal = {};
  // Holds grand totals for the project (all non-approved tasks)
  const projectTotal = {
    materialCost: 0.0,
    laborCost: 0.0,
    subcontractorCost: 0.0,
    materialSaleAmount: 0.0,
    laborSaleAmount: 0.0,
    subcontractorSaleAmount: 0.0,
    totalSaleAmount: 0.0,
    totalCost: 0.0,
    materialProfit: 0.0,
    laborProfit: 0.0,
    subcontractorProfit: 0.0,
    totalProfit: 0.0,
    materialProfitPercentage: 0.0,
    laborProfitPercentage: 0.0,
    subcontractorProfitPercentage: 0.0,
    totalProfitPercentage: 0.0,
    percentageComplete: 0.0,
    taskCount: 0,
  };

  // Default totals structure for a stage/section
  const defaultSectionTotals = {
    materialCost: 0.0,
    laborCost: 0.0,
    subcontractorCost: 0.0,
    materialSaleAmount: 0.0,
    laborSaleAmount: 0.0,
    subcontractorSaleAmount: 0.0,
    totalSaleAmount: 0.0,
    totalCost: 0.0,
    materialProfit: 0.0,
    laborProfit: 0.0,
    subcontractorProfit: 0.0,
    totalProfit: 0.0,
    materialProfitPercentage: 0.0,
    laborProfitPercentage: 0.0,
    subcontractorProfitPercentage: 0.0,
    totalProfitPercentage: 0.0,
    percentageComplete: 0.0,
    taskCount: 0,
    parentStageId: null,
    stageId: null,
    stageName: "",
    stageDescription: "",
  };

  // Default totals structure for a task list
  const defaultTaskListTotal = {
    materialCost: 0.0,
    laborCost: 0.0,
    subcontractorCost: 0.0,
    materialSaleAmount: 0.0,
    laborSaleAmount: 0.0,
    subcontractorSaleAmount: 0.0,
    totalSaleAmount: 0.0,
    totalCost: 0.0,
    materialProfit: 0.0,
    laborProfit: 0.0,
    subcontractorProfit: 0.0,
    totalProfit: 0.0,
    materialProfitPercentage: 0.0,
    laborProfitPercentage: 0.0,
    subcontractorProfitPercentage: 0.0,
    totalProfitPercentage: 0.0,
    percentageComplete: 0.0,
    taskCount: 0,
  };

  // Helper to calculate profit percentage, safe for zero cost
  /**
   * Calculates the profit percentage relative to sale amount
   * @param {number|string} profit - The profit amount (can be number, string, or Decimal)
   * @param {number|string} saleAmount - The total sale amount (can be number, string, or Decimal)
   * @returns {Decimal} The profit percentage as a Decimal (e.g., 25.5 for 25.5%)
   *
   * @example
   * // Returns Decimal(25.0) for 25% profit
   * toPercent(250, 1000);
   *
   * @description
   * - Uses toDecimal() to safely convert inputs to Decimal for precise calculations
   * - Handles division by zero by checking if saleAmount is zero
   * - Returns a Decimal instance for consistent precision in further calculations
   */
  const toPercent = (profit, saleAmount) => {
    // Convert inputs to Decimal for precise decimal arithmetic
    // toDecimal() handles null/undefined by converting them to 0
    const _profit = toDecimal(profit);
    const _saleAmount = toDecimal(saleAmount);

    // Calculate percentage: (profit / saleAmount) * 100
    // Each operation returns a new Decimal instance for method chaining
    const profitPercentage = toDecimal(_profit)
      .dividedBy(_saleAmount) // Safe division (returns Decimal)
      .times(100); // Convert to percentage

    // Prevent division by zero - return 0% if sale amount is zero
    if (_saleAmount.isZero()) {
      return toDecimal(0);
    }

    return profitPercentage; // Returns Decimal instance with calculated percentage
  };

  // Ensure the totals object exists for a given stage
  const initializeTotals = (target, id, parentId = null, stageData = {}) => {
    if (!target[id]) {
      target[id] = {
        ...defaultSectionTotals,
        parentStageId: parentId,
        stageId: id,
        stageName: stageData.name || "",
        stageDescription: stageData.description || "",
        stageHierarchy: stageData.sectionHierarchy || "",
      };
    }
  };

  // Process all tasks for a given stage, aggregating their values
  const processTasks = (tasks, stageId) => {
    initializeTotals(stageTotal, stageId);

    if (!stageTotal[stageId].taskList) {
      // Each stage gets a taskList object to hold all its tasks and a running total
      stageTotal[stageId].taskList = {
        tasks: {},
        total: { ...defaultTaskListTotal },
      };
    }

    // Loop through all tasks in this stage
    tasks.forEach((task) => {
      const taskId = task.id;

      // Build the intial taskData object for this task, rounding all values for accuracy
      const taskData = {
        materialCost: roundValue(task.materialTotal_Cost || 0),
        laborCost: roundValue(task.laborTotal_Cost || 0),
        subcontractorCost: roundValue(task.subcontractorTotal_Cost || 0),
        materialSaleAmount: roundValue(task.materialTotal_SaleAmount || 0),
        laborSaleAmount: roundValue(task.laborTotal_SaleAmount || 0),
        subcontractorSaleAmount: roundValue(
          task.subcontractorTotal_SaleAmount || 0
        ),
        totalSaleAmount: roundValue(0),
        totalCost: roundValue(0),
        materialProfit: roundValue(task.material_Profit || 0),
        laborProfit: roundValue(task.labor_Profit || 0),
        subcontractorProfit: roundValue(task.subcontractor_Profit || 0),
        totalProfit: roundValue(task.totalProfit || 0),
        percentageComplete: roundValue(task.percentageComplete || 0),
        taskCount: 1,
        parentStageId: stageId,
        stageId: stageId,
        stageName: "",
        stageDescription: "",
        name: task.name,
        id: task.id,
      };

      // Calculate and assign total sale amount, cost, and profit for this task
      // Use Decimal.js for all additions to avoid floating-point precision errors
      taskData.totalSaleAmount = roundValue(
        toDecimal(roundValue(taskData.materialSaleAmount))
          .plus(toDecimal(roundValue(taskData.laborSaleAmount)))
          .plus(toDecimal(roundValue(taskData.subcontractorSaleAmount)))
      );

      taskData.totalCost = roundValue(
        toDecimal(taskData.materialCost)
          .plus(toDecimal(taskData.laborCost))
          .plus(toDecimal(taskData.subcontractorCost))
      );
      taskData.totalProfit = roundValue(
        task.totalProfit
          ? toDecimal(task.totalProfit)
          : toDecimal(taskData.materialProfit)
            .plus(toDecimal(taskData.laborProfit))
            .plus(toDecimal(taskData.subcontractorProfit))
      );

      // Add this task's data to the stage's task list
      stageTotal[stageId].taskList.tasks[taskId] = taskData;

      const isVariation = task.isVariation === true || task.isOption === true;

      if (!isVariation) {
        // Aggregate this task's values into the stage totals
        // Use Decimal.js for all additions to avoid floating-point precision errors
        stageTotal[stageId].materialCost = roundValue(
          toDecimal(stageTotal[stageId].materialCost).plus(toDecimal(taskData.materialCost))
        );
        stageTotal[stageId].laborCost = roundValue(
          toDecimal(stageTotal[stageId].laborCost).plus(toDecimal(taskData.laborCost))
        );
        stageTotal[stageId].subcontractorCost = roundValue(
          toDecimal(stageTotal[stageId].subcontractorCost).plus(toDecimal(taskData.subcontractorCost))
        );
        stageTotal[stageId].materialSaleAmount = roundValue(
          toDecimal(stageTotal[stageId].materialSaleAmount).plus(toDecimal(taskData.materialSaleAmount))
        );
        stageTotal[stageId].laborSaleAmount = roundValue(
          toDecimal(stageTotal[stageId].laborSaleAmount).plus(toDecimal(taskData.laborSaleAmount))
        );
        stageTotal[stageId].subcontractorSaleAmount = roundValue(
          toDecimal(stageTotal[stageId].subcontractorSaleAmount).plus(toDecimal(taskData.subcontractorSaleAmount))
        );
        stageTotal[stageId].totalSaleAmount = roundValue(
          toDecimal(stageTotal[stageId].totalSaleAmount).plus(toDecimal(taskData.totalSaleAmount))
        );
        stageTotal[stageId].totalCost = roundValue(
          toDecimal(stageTotal[stageId].totalCost).plus(toDecimal(taskData.totalCost))
        );
        stageTotal[stageId].materialProfit = roundValue(
          toDecimal(stageTotal[stageId].materialProfit).plus(toDecimal(taskData.materialProfit))
        );
        stageTotal[stageId].laborProfit = roundValue(
          toDecimal(stageTotal[stageId].laborProfit).plus(toDecimal(taskData.laborProfit))
        );
        stageTotal[stageId].subcontractorProfit = roundValue(
          toDecimal(stageTotal[stageId].subcontractorProfit).plus(toDecimal(taskData.subcontractorProfit))
        );
        stageTotal[stageId].totalProfit = roundValue(
          toDecimal(stageTotal[stageId].totalProfit).plus(toDecimal(taskData.totalProfit))
        );
        stageTotal[stageId].percentageComplete = roundValue(
          toDecimal(stageTotal[stageId].percentageComplete).plus(toDecimal(taskData.percentageComplete))
        );
        stageTotal[stageId].taskCount += 1;
      }

      // Only aggregate base (non-variation) tasks into the project total
      if (!task.proposal_status) {
        // Use Decimal.js for all additions to avoid floating-point precision errors
        projectTotal.materialCost = roundValue(
          toDecimal(projectTotal.materialCost).plus(toDecimal(taskData.materialCost))
        );
        projectTotal.laborCost = roundValue(
          toDecimal(projectTotal.laborCost).plus(toDecimal(taskData.laborCost))
        );
        projectTotal.subcontractorCost = roundValue(
          toDecimal(projectTotal.subcontractorCost).plus(toDecimal(taskData.subcontractorCost))
        );
        projectTotal.materialSaleAmount = roundValue(
          toDecimal(projectTotal.materialSaleAmount).plus(toDecimal(taskData.materialSaleAmount))
        );
        projectTotal.laborSaleAmount = roundValue(
          toDecimal(projectTotal.laborSaleAmount).plus(toDecimal(taskData.laborSaleAmount))
        );
        projectTotal.subcontractorSaleAmount = roundValue(
          toDecimal(projectTotal.subcontractorSaleAmount).plus(toDecimal(taskData.subcontractorSaleAmount))
        );
        projectTotal.totalSaleAmount = roundValue(
          toDecimal(projectTotal.totalSaleAmount).plus(toDecimal(taskData.totalSaleAmount))
        );
        projectTotal.totalCost = roundValue(
          toDecimal(projectTotal.totalCost).plus(toDecimal(taskData.totalCost))
        );
        projectTotal.materialProfit = roundValue(
          toDecimal(projectTotal.materialProfit).plus(toDecimal(taskData.materialProfit))
        );
        projectTotal.laborProfit = roundValue(
          toDecimal(projectTotal.laborProfit).plus(toDecimal(taskData.laborProfit))
        );
        projectTotal.subcontractorProfit = roundValue(
          toDecimal(projectTotal.subcontractorProfit).plus(toDecimal(taskData.subcontractorProfit))
        );
        projectTotal.totalProfit = roundValue(
          toDecimal(projectTotal.totalProfit).plus(toDecimal(taskData.totalProfit))
        );
        projectTotal.percentageComplete = roundValue(
          toDecimal(projectTotal.percentageComplete).plus(toDecimal(taskData.percentageComplete))
        );
        projectTotal.taskCount += 1;

        // Add to taskList.total for non-APPROVED tasks (for this stage)
        // Use Decimal.js for all additions to avoid floating-point precision errors
        let tlt = stageTotal[stageId].taskList.total;
        tlt.materialCost = roundValue(toDecimal(tlt.materialCost).plus(toDecimal(taskData.materialCost)));
        tlt.laborCost = roundValue(toDecimal(tlt.laborCost).plus(toDecimal(taskData.laborCost)));
        tlt.subcontractorCost = roundValue(
          toDecimal(tlt.subcontractorCost).plus(toDecimal(taskData.subcontractorCost))
        );
        tlt.materialSaleAmount = roundValue(
          toDecimal(tlt.materialSaleAmount).plus(toDecimal(taskData.materialSaleAmount))
        );
        tlt.laborSaleAmount = roundValue(
          toDecimal(tlt.laborSaleAmount).plus(toDecimal(taskData.laborSaleAmount))
        );
        tlt.subcontractorSaleAmount = roundValue(
          toDecimal(tlt.subcontractorSaleAmount).plus(toDecimal(taskData.subcontractorSaleAmount))
        );
        tlt.totalSaleAmount = roundValue(
          toDecimal(tlt.totalSaleAmount).plus(toDecimal(taskData.totalSaleAmount))
        );
        tlt.totalCost = roundValue(toDecimal(tlt.totalCost).plus(toDecimal(taskData.totalCost)));
        tlt.materialProfit = roundValue(
          toDecimal(tlt.materialProfit).plus(toDecimal(taskData.materialProfit))
        );
        tlt.laborProfit = roundValue(toDecimal(tlt.laborProfit).plus(toDecimal(taskData.laborProfit)));
        tlt.subcontractorProfit = roundValue(
          toDecimal(tlt.subcontractorProfit).plus(toDecimal(taskData.subcontractorProfit))
        );
        tlt.totalProfit = roundValue(toDecimal(tlt.totalProfit).plus(toDecimal(taskData.totalProfit)));
        tlt.percentageComplete = roundValue(
          toDecimal(tlt.percentageComplete).plus(toDecimal(taskData.percentageComplete))
        );
        tlt.taskCount += 1;
      }
    });
  };

  // Aggregate a child stage's totals into its parent stage
  // Use Decimal.js for all additions to avoid floating-point precision errors
  const rollUpToParent = (parentId, childTotals) => {
    initializeTotals(stageTotal, parentId);
    stageTotal[parentId].materialCost = roundValue(
      toDecimal(stageTotal[parentId].materialCost).plus(toDecimal(childTotals.materialCost))
    );
    stageTotal[parentId].laborCost = roundValue(
      toDecimal(stageTotal[parentId].laborCost).plus(toDecimal(childTotals.laborCost))
    );
    stageTotal[parentId].subcontractorCost = roundValue(
      toDecimal(stageTotal[parentId].subcontractorCost).plus(toDecimal(childTotals.subcontractorCost))
    );
    stageTotal[parentId].materialSaleAmount = roundValue(
      toDecimal(stageTotal[parentId].materialSaleAmount).plus(toDecimal(childTotals.materialSaleAmount))
    );
    stageTotal[parentId].laborSaleAmount = roundValue(
      toDecimal(stageTotal[parentId].laborSaleAmount).plus(toDecimal(childTotals.laborSaleAmount))
    );
    stageTotal[parentId].subcontractorSaleAmount = roundValue(
      toDecimal(stageTotal[parentId].subcontractorSaleAmount).plus(toDecimal(childTotals.subcontractorSaleAmount))
    );
    stageTotal[parentId].totalSaleAmount = roundValue(
      toDecimal(stageTotal[parentId].totalSaleAmount).plus(toDecimal(childTotals.totalSaleAmount))
    );
    stageTotal[parentId].totalCost = roundValue(
      toDecimal(stageTotal[parentId].totalCost).plus(toDecimal(childTotals.totalCost))
    );
    stageTotal[parentId].materialProfit = roundValue(
      toDecimal(stageTotal[parentId].materialProfit).plus(toDecimal(childTotals.materialProfit))
    );
    stageTotal[parentId].laborProfit = roundValue(
      toDecimal(stageTotal[parentId].laborProfit).plus(toDecimal(childTotals.laborProfit))
    );
    stageTotal[parentId].subcontractorProfit = roundValue(
      toDecimal(stageTotal[parentId].subcontractorProfit).plus(toDecimal(childTotals.subcontractorProfit))
    );
    stageTotal[parentId].totalProfit = roundValue(
      toDecimal(stageTotal[parentId].totalProfit).plus(toDecimal(childTotals.totalProfit))
    );
    stageTotal[parentId].percentageComplete = roundValue(
      toDecimal(stageTotal[parentId].percentageComplete).plus(toDecimal(childTotals.percentageComplete))
    );
    stageTotal[parentId].taskCount += childTotals.taskCount;
  };

  // Recursively traverse all stages and their children, processing tasks and rolling up totals
  const traverseStages = (stages, parentId = null) => {
    if (!stages || !Array.isArray(stages)) return;
    stages.forEach((stage) => {
      const currentStageId = stage.id;
      initializeTotals(stageTotal, currentStageId, parentId, stage);
      if (Array.isArray(stage.tasks) && stage.tasks.length > 0) {
        processTasks(stage.tasks, currentStageId);
      }
      if (Array.isArray(stage.stages) && stage.stages.length > 0) {
        traverseStages(stage.stages, currentStageId);
      }
      if (parentId) {
        rollUpToParent(parentId, stageTotal[currentStageId]);
      }
    });
  };

  traverseStages(stages);

  Object.keys(stageTotal).forEach((stageId) => {
    const summary = stageTotal[stageId];

    if (summary.taskCount > 0) {
      // For material profit percentage
      summary.materialProfitPercentage = roundValue(
        toPercent(summary.materialProfit, summary.materialSaleAmount)
      );

      // For labor profit percentage
      summary.laborProfitPercentage = roundValue(
        toPercent(summary.laborProfit, summary.laborSaleAmount)
      );

      // For subcontractor profit percentage
      summary.subcontractorProfitPercentage = roundValue(
        toPercent(summary.subcontractorProfit, summary.subcontractorSaleAmount)
      );

      // For total profit percentage
      summary.totalProfitPercentage = roundValue(
        toPercent(summary.totalProfit, summary.totalSaleAmount)
      );
      summary.percentageComplete = roundValue(
        summary.percentageComplete / summary.taskCount
      );
    }

    if (summary.taskList && summary.taskList.tasks) {
      Object.keys(summary.taskList.tasks).forEach((taskId) => {
        const task = summary.taskList.tasks[taskId];
        task.materialProfitPercentage = roundValue(
          toPercent(task.materialProfit, task.materialSaleAmount)
        );
        task.laborProfitPercentage = roundValue(
          toPercent(task.laborProfit, task.laborSaleAmount)
        );
        task.subcontractorProfitPercentage = roundValue(
          toPercent(task.subcontractorProfit, task.subcontractorSaleAmount)
        );
        task.totalProfitPercentage = roundValue(
          toPercent(task.totalProfit, task.totalSaleAmount)
        );
      });
      // Calculate percentages for taskList.total
      const taskListTotal = summary.taskList.total;
      if (taskListTotal.taskCount > 0) {
        taskListTotal.materialProfitPercentage = roundValue(
          toPercent(
            taskListTotal.materialProfit,
            taskListTotal.materialSaleAmount
          )
        );
        taskListTotal.laborProfitPercentage = roundValue(
          toPercent(taskListTotal.laborProfit, taskListTotal.laborSaleAmount)
        );
        taskListTotal.subcontractorProfitPercentage = roundValue(
          toPercent(
            taskListTotal.subcontractorProfit,
            taskListTotal.subcontractorSaleAmount
          )
        );
        taskListTotal.totalProfitPercentage = roundValue(
          toPercent(taskListTotal.totalProfit, taskListTotal.totalSaleAmount)
        );
        taskListTotal.percentageComplete = roundValue(
          taskListTotal.percentageComplete / taskListTotal.taskCount
        );
      }
    }
  });

  if (projectTotal.taskCount > 0) {
    projectTotal.materialProfitPercentage = roundValue(
      toPercent(projectTotal.materialProfit, projectTotal.materialSaleAmount)
    );
    projectTotal.laborProfitPercentage = roundValue(
      toPercent(projectTotal.laborProfit, projectTotal.laborSaleAmount)
    );
    projectTotal.subcontractorProfitPercentage = roundValue(
      toPercent(
        projectTotal.subcontractorProfit,
        projectTotal.subcontractorSaleAmount
      )
    );
    projectTotal.totalProfitPercentage = roundValue(
      toPercent(projectTotal.totalProfit, projectTotal.totalSaleAmount)
    );
    projectTotal.percentageComplete = roundValue(
      projectTotal.percentageComplete / projectTotal.taskCount
    );
  }

  return {
    sections: stageTotal,
    projectTotal: projectTotal,
  };
};
